/**
 * Sample drift checks.
 *
 * The set is chosen so the screen has to keep the three states apart. One
 * model has genuinely drifted, one is holding, and one CANNOT SAY — its window
 * is mostly predictions whose horizon has not elapsed. That third row is the
 * one a dashboard turns green by accident, and it is the reason the module
 * exists.
 */
import type { DriftCheck, DriftSchedule } from "../domain/drift";

const ago = (days: number) => new Date(Date.now() - days * 86400000).toISOString();

export function mockDriftChecks(): DriftCheck[] {
  return [
    {
      id: 1,
      modelKey: "lamp_survival",
      windowFrom: ago(400),
      windowTo: ago(35),
      predictions: 150,
      decided: 150,
      undecided: 0,
      pending: 0,
      judgeableFraction: 1,
      changePoint: "2026-04-18",
      verdict:
        "This model has drifted on calibration and performance. Precision fell from 89% to 27% around " +
        "2026-04-18. The model predicts 80% on average and 45% actually happen — it is systematically " +
        "over-confident by 35% on this window. Re-baselining would make this check green without making " +
        "the model right — the finding is that it needs refitting, or that the estate changed in a way " +
        "somebody should explain.",
      conclusive: true,
      checkedAt: ago(2),
    },
    {
      id: 2,
      modelKey: "pavement_pci",
      windowFrom: ago(400),
      windowTo: ago(35),
      predictions: 96,
      decided: 88,
      undecided: 8,
      pending: 0,
      judgeableFraction: 0.92,
      verdict:
        "Inputs have moved — the burn-hours distribution has shifted by a year — but calibration and " +
        "measured performance are both unchanged. Input movement is a reason to look, never a finding " +
        "on its own. This model still describes the estate.",
      conclusive: true,
      checkedAt: ago(2),
    },
    {
      id: 3,
      modelKey: "agl_isolation_risk",
      windowFrom: ago(90),
      windowTo: ago(0),
      predictions: 64,
      decided: 7,
      undecided: 0,
      /* the blind spot: a ninety-day horizon predicted last week cannot have
         been scored, so the period everybody wants covered says nothing */
      pending: 57,
      judgeableFraction: 0.11,
      verdict:
        "Only 7 of 64 predictions in this window have had their horizon elapse. The rest are not right " +
        "and not wrong — nobody has found out yet. This check cannot support a conclusion in either " +
        "direction, and reading it as 'no drift' would be reading an empty window as a clean one.",
      conclusive: false,
      checkedAt: ago(1),
    },
  ];
}

export function mockDriftSchedule(): DriftSchedule {
  return {
    due: [{ modelKey: "friction_decay", horizonDays: 90, windowDays: 360, everyDays: 45, lastChecked: undefined }],
    waiting: [
      { modelKey: "lamp_survival", horizonDays: 90, windowDays: 360, everyDays: 45, lastChecked: ago(2) },
      { modelKey: "pavement_pci", horizonDays: 90, windowDays: 360, everyDays: 45, lastChecked: ago(2) },
    ],
  };
}
